import React, { useMemo, useState, useEffect, useCallback } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Rocket, Globe2, MessageSquare, Calendar, BellRing,
  Users, Building2, Truck, Store, Target,
  GraduationCap, Megaphone, BookOpen, Trophy, Award,
  ClipboardList, ShoppingBag, BarChart3, DollarSign, FileText, Settings as SettingsIcon,
  Heart, Home, Wallet, ShieldCheck, ChevronDown, Package, Mail,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BrandMark from "@/components/lifted/BrandMark";
import api from "@/lib/api";

/**
 * Phase 12 — Collapsible role-aware sidebar.
 *
 * • Every section is collapsible (chevron rotates).
 * • The section that owns the active route auto-expands.
 * • Per-user expand/collapse state persists in localStorage.
 * • A child route is "active" if its `to` matches the current path; the
 *   parent is "current section" but never lights up as active.
 * • No duplicates: an item appears once across the sidebar; if the same
 *   route belongs in multiple zones (e.g. /messages), it stays in the most
 *   role-relevant group.
 */

const A = ["admin", "master_admin"];
const ALL = ["all"];

// ---------------------------------------------------------------------------
// Section model
// Phase 32 — Customers see ONLY Main → Dashboard + Tools → Settings.
// Other sections are role-scoped and won't appear for customer-only users.
// ---------------------------------------------------------------------------
const SECTIONS = [
  {
    key: "main",
    label: "Main",
    items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", roles: ALL },
      { to: "/ecosystem-map", icon: Globe2, label: "Ecosystem Map", roles: ["affiliate","driver","partner","franchisee","lifted_partner", ...A] },
      { to: "/events", icon: Calendar, label: "Events & Webinars", roles: ["affiliate","driver","partner","franchisee","lifted_partner","trainer", ...A] },
    ],
  },
  // ----- Network & Messages ------------------------------------------------
  {
    key: "network",
    label: "Network & Messages",
    items: [
      { to: "/messages",         icon: MessageSquare, label: "Inbox",            roles: ["affiliate","driver","partner","franchisee","lifted_partner","trainer", ...A] },
      { to: "/network",          icon: Rocket,        label: "Lifted Network",   roles: ["affiliate","driver","partner","franchisee","lifted_partner", ...A] },
      { to: "/network/members",  icon: Users,         label: "Member Directory", roles: ["affiliate","driver","partner","franchisee","lifted_partner", ...A] },
      { to: "/network/profile",  icon: LayoutDashboard, label: "My Network Profile", roles: ["affiliate","driver","partner","franchisee","lifted_partner", ...A] },
      { to: "/admin/broadcast",  icon: Megaphone,     label: "Broadcasts",       roles: A },
    ],
  },
  // ----- Affiliate Zone ----------------------------------------------------
  {
    key: "affiliate",
    label: "Affiliate Zone",
    items: [
      { to: "/affiliate",                icon: Rocket,      label: "Dashboard",         roles: ["affiliate", ...A] },
      { to: "/affiliate/links",          icon: ShoppingBag, label: "Product Links",     roles: ["affiliate", ...A] },
      { to: "/affiliate/tracking",       icon: Target,      label: "Tracking & Leads",  roles: ["affiliate", ...A] },
      { to: "/affiliate/commissions",    icon: DollarSign,  label: "Commissions",       roles: ["affiliate", ...A] },
      { to: "/affiliate/payouts",        icon: Wallet,      label: "Payouts",           roles: ["affiliate", ...A] },
      { to: "/affiliate/supplies",       icon: Truck,       label: "Same-Day Products", roles: ["affiliate", ...A] },
      { to: "/affiliate/marketing",      icon: Megaphone,   label: "Marketing Tools",   roles: ["affiliate", ...A] },
    ],
  },
  // ----- Driver -----------------------------------------------------------
  {
    key: "driver",
    label: "Driver Zone",
    items: [
      { to: "/delivery",                icon: Truck,         label: "Driver Dashboard",   roles: ["driver"] },
      { to: "/delivery#available",      icon: Truck,         label: "Available Orders",   roles: ["driver"] },
      { to: "/delivery#assigned",       icon: ClipboardList, label: "Assigned Orders",    roles: ["driver"] },
      { to: "/delivery#documents",      icon: FileText,      label: "Driver Documents",   roles: ["driver"] },
      { to: "/admin/supplies/live-map", icon: Globe2,        label: "Delivery Map",       roles: ["driver"] },
    ],
  },
  // ----- Partner ----------------------------------------------------------
  {
    key: "partner",
    label: "Partner Zone",
    items: [
      { to: "/partner",            icon: Store,         label: "Partner Dashboard",  roles: ["partner"] },
      { to: "/partner/inventory",  icon: ShoppingBag,   label: "Inventory",          roles: ["partner"] },
      { to: "/partner/products/submit", icon: ShoppingBag, label: "Upload Product", roles: ["partner"] },
      { to: "/partner/orders",     icon: ClipboardList, label: "Orders",             roles: ["partner"] },
      { to: "/partner/storefront", icon: Store,         label: "Partner Storefront", roles: ["partner"] },
      { to: "/partner/compliance", icon: ShieldCheck,   label: "Compliance",         roles: ["partner"] },
      { to: "/admin/supplies/live-map", icon: Globe2,   label: "Delivery Map",       roles: ["partner"] },
    ],
  },
  // ----- Franchisee --------------------------------------------------------
  {
    key: "franchise",
    label: "Franchise Zone",
    items: [
      { to: "/franchise",           icon: Building2,     label: "Franchise Dashboard", roles: ["franchisee"] },
      { to: "/franchise/territory", icon: Globe2,        label: "Territory",           roles: ["franchisee"] },
      { to: "/franchise/partners",  icon: Store,         label: "Partner Stores",      roles: ["franchisee"] },
      { to: "/franchise/drivers",   icon: Truck,         label: "Drivers",             roles: ["franchisee"] },
      { to: "/franchise/orders",    icon: ClipboardList, label: "Orders",              roles: ["franchisee"] },
      { to: "/admin/supplies/live-map", icon: Globe2,    label: "Delivery Map",        roles: ["franchisee"] },
      { to: "/reports",             icon: BarChart3,     label: "Reports",             roles: ["franchisee"] },
    ],
  },
  // ----- Lifted Partner (Community Mentor / Phase 17) ---------------------
  {
    key: "lifted_partner",
    label: "Lifted Partner",
    items: [
      { to: "/lifted-partner",            icon: Heart,         label: "Mentor Dashboard",  roles: ["lifted_partner", ...A] },
      { to: "/lifted-partner/affiliates", icon: Users,         label: "Affiliates",        roles: ["lifted_partner", ...A] },
      { to: "/lifted-partner/pending",    icon: ClipboardList, label: "Pending Reviews",   roles: ["lifted_partner", ...A] },
      { to: "/lifted-partner/training",   icon: GraduationCap, label: "Training Progress", roles: ["lifted_partner", ...A] },
    ],
  },
  // ----- Training Center --------------------------------------------------
  {
    key: "training",
    label: "Training Center",
    items: [
      { to: "/training",                      icon: GraduationCap, label: "Training Home",        roles: ["affiliate","driver","partner","franchisee","trainer","teacher", ...A] },
      { to: "/training/affiliate",            icon: BookOpen,      label: "Affiliate Curriculum", roles: ["affiliate", ...A] },
      { to: "/training/driver",               icon: BookOpen,      label: "Driver Curriculum",    roles: ["driver", ...A] },
      { to: "/training/partner",              icon: BookOpen,      label: "Partner Curriculum",   roles: ["partner", ...A] },
      { to: "/training/franchise",            icon: BookOpen,      label: "Franchise Curriculum", roles: ["franchisee", ...A] },
      { to: "/training/legacy",               icon: BookOpen,      label: "Legacy Training",      roles: ["affiliate","driver","partner","franchisee","trainer","teacher", ...A] },
      // Admin-only training management
      { to: "/admin/training/modules",        icon: BookOpen,      label: "Modules (Admin)",      roles: A },
      { to: "/admin/training/certifications", icon: Award,         label: "Certifications",       roles: A },
      { to: "/admin/training/progress",       icon: BarChart3,     label: "Progress Reports",     roles: A },
      { to: "/admin/certificates",            icon: Award,         label: "Certificate Moderation", roles: A },
      { to: "/admin/training/announcements",  icon: Megaphone,     label: "Announcements",        roles: A },
      { to: "/admin/training/access-codes",   icon: FileText,      label: "Access Codes",         roles: A },
    ],
  },
  // ----- LiftedAtTheMoment store admin ------------------------------------
  {
    key: "store",
    label: "LiftedAtTheMoment",
    items: [
      { to: "/admin/shop",                  icon: Store,         label: "Storefront Overview", roles: A },
      // Phase 22 — single consolidated Product Management entry replaces the
      // legacy Inventory / Products / Approvals / Media links. The page
      // exposes those views as tabs internally. Direct routes still work.
      { to: "/admin/product-management",    icon: Package,       label: "Product Management",   roles: A },
      { to: "/admin/compliance",            icon: ShieldCheck,   label: "AI Compliance",        roles: A },
      { to: "/admin/collections",           icon: BookOpen,      label: "Collections",          roles: A },
      { to: "/admin/orders",                icon: ClipboardList, label: "Orders",               roles: A },
      { to: "/admin/customers",             icon: Users,         label: "Customers",            roles: A },
      { to: "/admin/reports",               icon: BarChart3,     label: "Reports & Analytics",  roles: A },
      { to: "/admin/brand-assets",          icon: FileText,      label: "Brand Assets",         roles: A },
      { to: "/admin/site",                  icon: SettingsIcon,  label: "Public Site Settings", roles: A },
    ],
  },
  // ----- Lifted Supplies ---------------------------------------------------
  {
    key: "supplies",
    label: "Lifted Supplies",
    items: [
      { to: "/admin/supplies",                 icon: Truck,         label: "Supplies Dashboard",  roles: A },
      { to: "/admin/supplies/same-day",        icon: Truck,         label: "Same-Day Delivery",   roles: A },
      { to: "/admin/supplies/live-map",        icon: Globe2,        label: "Live Driver Map",     roles: A },
      { to: "/admin/supplies/drivers",         icon: Truck,         label: "Drivers",             roles: A },
      { to: "/admin/supplies/applications",    icon: ClipboardList, label: "Driver Applications", roles: A },
      { to: "/admin/supplies/orders",          icon: ClipboardList, label: "Delivery Orders",     roles: A },
      { to: "/admin/supplies/dispatch",        icon: ClipboardList, label: "Delivery Control",    roles: A },
      { to: "/admin/partners",                 icon: Store,         label: "Partner Stores",      roles: A },
      { to: "/admin/partners/applications",    icon: Store,         label: "Partner Applications", roles: A },
      { to: "/admin/franchises/applications",  icon: Building2,     label: "Franchise Applications", roles: A },
      { to: "/admin/locations",                icon: Globe2,        label: "Locations",           roles: A },
      { to: "/admin/zones",                    icon: Globe2,        label: "Delivery Zones",      roles: A },
    ],
  },
  // ----- Admin: Affiliates -------------------------------------------------
  {
    key: "admin_affiliates",
    label: "Admin · Affiliates",
    items: [
      { to: "/admin/affiliate-command",  icon: BarChart3, label: "Command Center",      roles: A },
      { to: "/admin/affiliate-billing",  icon: DollarSign, label: "Billing Controls",    roles: A },
      { to: "/admin/affiliate",          icon: Users,     label: "Affiliate Profiles",  roles: A },
    ],
  },
  // ----- Admin Settings ----------------------------------------------------
  {
    key: "admin_settings",
    label: "Admin Settings",
    items: [
      { to: "/admin/users",        icon: Users,        label: "Users",                roles: A },
      { to: "/admin/roles",        icon: Award,        label: "Roles & Permissions",  roles: ["master_admin"] },
      { to: "/admin/access-codes", icon: FileText,     label: "Access Codes",         roles: A },
      // Master Admin only — Phase 32 restricted scope
      { to: "/admin/contracts",    icon: FileText,     label: "Contracts",            roles: ["master_admin"] },
      { to: "/admin/email-settings", icon: Mail,       label: "Email Settings",       roles: ["master_admin"] },
      { to: "/admin/readiness",    icon: BarChart3,    label: "System Readiness",     roles: ["master_admin"] },
      { to: "/admin/data-cleanup", icon: ShieldCheck,  label: "Data Cleanup",         roles: ["master_admin"] },
      { to: "/admin/integrations", icon: SettingsIcon, label: "API / Integrations",   roles: ["master_admin"] },
      { to: "/admin/finances",     icon: DollarSign,   label: "Finances",             roles: ["master_admin"] },
      { to: "/admin/settings",     icon: SettingsIcon, label: "Admin Settings",       roles: A },
    ],
  },
  // ----- Tools & Resources -------------------------------------------------
  {
    key: "tools",
    label: "Tools & Resources",
    items: [
      { to: "/resources",            icon: BookOpen, label: "Resources",     roles: ["affiliate","driver","partner","franchisee","lifted_partner","trainer","teacher", ...A] },
      { to: "/achievements",         icon: Award,    label: "Achievements",  roles: ["affiliate","driver","partner","franchisee", ...A] },
      { to: "/leaderboard",          icon: Trophy,   label: "Leaderboard",   roles: ["affiliate","driver","partner","franchisee", ...A] },
      { to: "/dashboard/contracts",  icon: FileText, label: "My Contracts",  roles: ["affiliate","driver","partner","franchisee","lifted_partner"] },
      { to: "/settings",             icon: SettingsIcon, label: "Settings",  roles: ALL },
    ],
  },
];

function visible(item, userRoles) {
  if (item.roles.includes("all")) return true;
  return item.roles.some((r) => userRoles.includes(r));
}

// localStorage helpers ------------------------------------------------------
const LS_KEY = "lifted_sidebar_open_groups_v2";
function readOpen() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_) { return {}; }  // eslint-disable-line no-unused-vars
}
function writeOpen(state) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch (_) {}  // eslint-disable-line no-unused-vars
}

// ---------------------------------------------------------------------------
function pathMatchesItem(pathname, search, item) {
  // Exact dashboard match
  if (item.to === "/dashboard") return pathname === "/dashboard";
  // Items with ?tab= rely on both path and tab
  if (item.to.includes("?")) {
    const [p, q] = item.to.split("?");
    if (pathname !== p) return false;
    const params = new URLSearchParams(search);
    const itemParams = new URLSearchParams(q);
    for (const [k, v] of itemParams) {
      if (params.get(k) !== v) return false;
    }
    return true;
  }
  // Items with #fragment ignore the fragment for path matching
  if (item.to.includes("#")) {
    return pathname === item.to.split("#")[0];
  }
  return pathname === item.to || pathname.startsWith(item.to + "/");
}

export default function DashboardSidebar({ mobile = false, embedded = false, onItemClick, hideFooter = false, unreadCount = 0 }) {
  const { user, activeRole } = useAuth();
  const location = useLocation();
  const [openMap, setOpenMap] = useState(() => readOpen());

  const effectiveRoles = useMemo(() => {
    if (!user) return [];
    if ((user.roles || []).includes("master_admin") && activeRole && activeRole !== "master_admin") {
      return [activeRole, "all"];
    }
    return user.roles || [];
  }, [user, activeRole]);

  const sections = useMemo(() => {
    return SECTIONS.map((s) => ({
      ...s,
      items: s.items.filter((i) => visible(i, effectiveRoles)),
    })).filter((s) => s.items.length > 0);
  }, [effectiveRoles]);

  // Auto-expand the section that owns the active route on every navigation.
  useEffect(() => {
    const activeSection = sections.find((s) =>
      s.items.some((it) => pathMatchesItem(location.pathname, location.search, it))
    );
    if (!activeSection) return;
    setOpenMap((prev) => {
      if (prev[activeSection.key]) return prev;
      const next = { ...prev, [activeSection.key]: true };
      writeOpen(next);
      return next;
    });
  }, [location.pathname, location.search, sections]);

  const toggleSection = useCallback((key) => {
    setOpenMap((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      writeOpen(next);
      return next;
    });
  }, []);

  // Iter81 — Admin Product Management pending-approval badge.
  // Poll lightweight count endpoint every 30s when an admin is logged in.
  // Hooks must run unconditionally (before the `if (!user)` early return).
  const isAdmin = !!user && (user.roles || []).some((r) => r === "admin" || r === "master_admin");
  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => {
    if (!isAdmin) return undefined;
    let alive = true;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/admin/products/pending/count");
        if (alive) setPendingCount(Number(data?.count || 0));
      } catch (_) { /* silent — keep last value */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [isAdmin]);

  if (!user) return null;

  // Phase 16.6 — `mobile` variant strips the `hidden lg:flex` so the same
  // navigation tree renders inside the AppShell mobile drawer. Desktop view
  // keeps its existing sticky sidebar untouched.
  // Phase 17 — `pb-24` removed when `hideFooter` is true because the
  // AppShell drawer now ships its own sticky footer (profile + sign-out)
  // and we don't want the extra padding pushing content under the footer.
  // Phase 21.3 — `embedded` means the parent (AppShell drawer) owns the
  // overflow scroll region. Drop our own `overflow-y-auto` so the user
  // doesn't end up with two competing scroll containers (which on iOS
  // Safari caused the profile/sign-out footer to float into the middle of
  // the menu).
  const asideClass = mobile
    ? `flex flex-col bg-black/95 w-full ${embedded ? "" : "overflow-y-auto"} ${hideFooter ? "pb-2" : "pb-24"}`
    : "hidden lg:flex flex-col bg-black/85 border-r border-white/5 backdrop-blur-md sticky top-0 h-screen w-64 overflow-y-auto";

  const formatBadge = (n) => (!n ? null : n > 99 ? "99+" : String(n));
  const inboxBadge = formatBadge(unreadCount);
  const pendingBadge = formatBadge(pendingCount);

  return (
    <aside
      className={asideClass}
      data-testid={mobile ? "mobile-sidebar" : "dashboard-sidebar"}
    >
      <div className="px-4 py-4 border-b border-white/5">
        <Link to="/dashboard" className="block" data-testid="sidebar-brand">
          <BrandMark tag="logo.latm.primary" size="sm" />
          <div className="font-bebas text-[0.55rem] tracking-[0.3em] text-lifted-dim mt-2">
            ECOSYSTEM CONSOLE
          </div>
        </Link>
      </div>

      <nav className="flex-1 px-2 py-3 space-y-2" data-testid="sidebar-nav">
        {sections.map((s) => {
          const isOpen = !!openMap[s.key];
          const sectionHasActive = s.items.some((it) =>
            pathMatchesItem(location.pathname, location.search, it)
          );
          return (
            <div key={s.key} data-testid={`sidebar-group-${s.key}`}>
              <button
                type="button"
                onClick={() => toggleSection(s.key)}
                aria-expanded={isOpen}
                className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded text-[0.65rem] font-bebas tracking-[0.22em] uppercase transition-colors ${
                  sectionHasActive ? "text-lifted-green" : "text-lifted-dim hover:text-white"
                }`}
                data-testid={`sidebar-group-toggle-${s.key}`}
              >
                <span className="truncate">{s.label}</span>
                <ChevronDown
                  size={12}
                  className={`shrink-0 transition-transform ${isOpen ? "rotate-180" : "rotate-0"}`}
                />
              </button>
              {isOpen && (
                <ul className="space-y-0.5 mt-1 mb-2" data-testid={`sidebar-group-items-${s.key}`}>
                  {s.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = pathMatchesItem(location.pathname, location.search, item);
                    const showInboxBadge = item.to === "/messages" && inboxBadge;
                    const showPendingBadge = item.to === "/admin/product-management" && pendingBadge;
                    return (
                      <li key={item.to + item.label}>
                        <NavLink
                          to={item.to}
                          end={item.to === "/dashboard"}
                          onClick={onItemClick}
                          className={`flex items-center gap-2 pl-4 pr-2 py-1.5 rounded text-xs transition-colors ${
                            isActive
                              ? "bg-lifted-green/15 text-lifted-green border-l-2 border-lifted-green"
                              : "text-white/80 hover:bg-white/5 hover:text-white"
                          }`}
                          data-testid={`sidebar-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          aria-current={isActive ? "page" : undefined}
                        >
                          <Icon size={14} className="shrink-0" />
                          <span className="truncate">{item.label}</span>
                          {showInboxBadge && (
                            <span
                              className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0"
                              data-testid="sidebar-inbox-unread"
                            >
                              {inboxBadge}
                            </span>
                          )}
                          {showPendingBadge && (
                            <span
                              className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0"
                              data-testid="sidebar-product-management-pending"
                              title={`${pendingCount} pending approval${pendingCount === 1 ? "" : "s"}`}
                            >
                              {pendingBadge}
                            </span>
                          )}
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </nav>

      {!hideFooter && (
      <div className="m-3 p-4 rounded-xl bg-gradient-to-br from-lifted-green/20 via-purple-500/10 to-black border border-lifted-green/30">
        <div className="flex items-center gap-2 mb-2">
          <Heart size={14} className="text-lifted-green" />
          <div className="font-bebas tracking-[0.18em] text-white text-xs">
            We're All In This Together
          </div>
        </div>
        <p className="text-[10px] text-lifted-dim mb-3">
          Invite a future affiliate, driver, partner, or franchisee and grow with the network.
        </p>
        <Link
          to="/affiliate-zone"
          className="block text-center bg-lifted-green text-black px-3 py-1.5 rounded text-[10px] font-bebas tracking-wider uppercase hover:bg-lifted-green/90"
          data-testid="invite-earn-cta"
        >
          Invite &amp; Earn
        </Link>
      </div>
      )}

      {!hideFooter && (
      <div className="px-4 py-3 text-[9px] text-lifted-dim border-t border-white/5">
        <div className="font-bebas tracking-[0.18em] text-white">Lifted At The Moment™</div>
        <div>One Ecosystem. Infinite Opportunities.</div>
        <Link to="/" className="mt-2 inline-flex items-center gap-1 text-lifted-green hover:underline">
          <Home size={9} /> Public site
        </Link>
      </div>
      )}
    </aside>
  );
}
